import { jsx } from 'react/jsx-runtime';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useFloating, offset, flip, shift, arrow, autoUpdate, useDismiss, useInteractions } from '@floating-ui/react';
import { useMediaQuery } from '@skatteetaten/ds-core-utils';
import { a as getPopoverPositionDefault, P as PopoverContent } from '../PopoverContent.esm.js';
export { g as getPopoverColorDefault, b as getPopoverRestoreFocusDefault } from '../PopoverContent.esm.js';
import { PopoverContext } from '../PopoverContext/index.esm.js';
import { PopoverTrigger } from '../PopoverTrigger/index.esm.js';
import 'react-i18next';
import '@skatteetaten/ds-buttons';
import '@skatteetaten/ds-icons';

const Popover = (props)=>{
    const { isOpen: controlledOpen, position = getPopoverPositionDefault(), disableAutoDismiss, disableAutoDismissOnMobile, children, onClose } = props;
    const arrowRef = useRef(null);
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = controlledOpen ?? internalOpen;
    const isMobile = !useMediaQuery('(min-width: 640px)');
    const shouldAutoDismiss = Boolean(!disableAutoDismiss && (!isMobile || !disableAutoDismissOnMobile && isMobile));
    const arrowLen = arrowRef.current?.offsetWidth ?? 0;
    const floatingOffset = Math.sqrt(2 * arrowLen ** 2) / 2;
    const kebabize = (str)=>str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    const floatingData = useFloating({
        open: isOpen,
        onOpenChange: (open)=>{
            setInternalOpen(open);
            if (!open) {
                onClose?.();
            }
        },
        placement: kebabize(position),
        whileElementsMounted: autoUpdate,
        middleware: [
            offset({
                mainAxis: floatingOffset,
                alignmentAxis: -32
            }),
            flip(),
            shift(),
            arrow({
                element: arrowRef
            })
        ]
    });
    const dismiss = useDismiss(floatingData.context, {
        enabled: shouldAutoDismiss,
        ancestorScroll: true
    });
    const interactions = useInteractions([
        dismiss
    ]);
    useEffect(()=>{
        if (!isOpen || !shouldAutoDismiss) {
            return;
        }
        const handleResize = ()=>{
            setInternalOpen(false);
            onClose?.();
        };
        window.addEventListener('resize', handleResize);
        return ()=>{
            window.removeEventListener('resize', handleResize);
        };
    }, [
        isOpen,
        shouldAutoDismiss,
        onClose
    ]);
    const value = useMemo(()=>({
            floatingData,
            interactions,
            arrowRef,
            isOpen,
            setIsOpen: setInternalOpen,
            isMobile,
            ...props
        }), [
        interactions,
        props,
        floatingData,
        arrowRef,
        isOpen,
        setInternalOpen,
        isMobile
    ]);
    return /*#__PURE__*/ jsx(PopoverContext.Provider, {
        value: value,
        children: children
    });
};
Popover.displayName = 'Popover';
Popover.Content = PopoverContent;
Popover.Content.displayName = 'Popover.Content';
Popover.Trigger = PopoverTrigger;
Popover.Trigger.displayName = 'Popover.Trigger';

export { Popover, getPopoverPositionDefault };
